import { useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import mixpanel from 'mixpanel-browser';
import {
  IUseHookAxiosError,
  IUseHookIsError,
  IUseHookIsFinished,
  IUseHookIsSuccess,
  IUseHookResponse,
  IUseHookErrorData,
} from 'hooks/@types';
import AuthService from 'services/Auth/Auth.service';
import UserService from 'services/User/User.service';
import {
  LOCAL_STORAGE_AUTH_TOKEN,
  LOCAL_STORAGE_USER_FORMSTACK_TOKEN,
  LOCAL_STORAGE_USER_ID,
} from 'common/localStorageKeys';
import IUseExternalToken, { IUseExternalTokenData, IUseExternalTokenProps } from './@types';

function useExternalToken(token?: string): IUseExternalToken {
  const abortController = new AbortController();
  const [loanding, setLoanding] = useState<boolean>(false);
  const [data, setData] = useState<IUseExternalTokenData | null>(null);

  const [isFinished, setIsFinished] = useState<IUseHookIsFinished>(false);

  const [isError, setIsError] = useState<IUseHookIsError>(null);
  const [isSuccess, setIsSuccess] = useState<IUseHookIsSuccess>(null);

  const [error, setError] = useState<IUseHookErrorData | null>(null);
  const [response, setResponse] = useState<IUseHookResponse>(null);
  const [axiosErrorResponse, setAxiosErrorResponse] = useState<IUseHookAxiosError>(null);

  const verify = async (values: IUseExternalTokenProps): Promise<AxiosResponse> => {
    // window.localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
    // window.localStorage.removeItem(LOCAL_STORAGE_USER_ID);
    window.localStorage.clear();
    mixpanel.reset();
    setResponse(null);
    setData(null);
    setLoanding(true);
    setIsFinished(false);
    setError(null);
    setIsSuccess(undefined);
    setIsError(undefined);
    setAxiosErrorResponse(null);

    return AuthService.verifyExternalToken({
      token: values?.token,
      signal: values?.signal || abortController.signal,
    })
      .then((responseRequest) => {
        if (responseRequest?.data?.status) {
          window.localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN, responseRequest?.data?.token);
          window.localStorage.setItem(LOCAL_STORAGE_USER_ID, responseRequest?.data?.user_id?.toString());
          if (responseRequest?.data?.show_password && responseRequest?.data?.temporal_password) {
            window.localStorage.setItem(LOCAL_STORAGE_USER_FORMSTACK_TOKEN, responseRequest?.data?.temporal_password);
          }
          UserService.getUser({
            id: responseRequest?.data?.user_id,
            signal: values.signal || abortController.signal,
          })
            .then((userResponseRequest) => {
              AuthService.setAuthDataWithToken({
                ignoreIdentify: true,
                token: responseRequest?.data?.token,
                user: userResponseRequest?.data?.data?.user,
              });
              setIsSuccess(true);
              setIsError(false);
              setData(responseRequest?.data);
              return userResponseRequest;
            })
            .catch((axiosError) => {
              setAxiosErrorResponse(axiosError);
              setIsSuccess(false);
              setIsError(true);
              setError(axiosError?.response?.data?.error_data);
              return axiosError;
            })
            .finally(() => {
              setLoanding(false);
              setIsFinished(true);
              return false;
            });
        } else {
          setIsSuccess(false);
          setIsError(true);
          setLoanding(false);
          setIsFinished(true);
        }

        return responseRequest;
      })
      .catch((axiosError) => {
        setAxiosErrorResponse(axiosError);
        setIsSuccess(false);
        setIsError(true);
        setError(axiosError?.response?.data);
        setLoanding(false);
        setIsFinished(true);
        return axiosError;
      });
  };

  useMemo(() => {
    if (!data && !loanding && token) {
      verify({
        token,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(
    () => () => {
      abortController.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    response,
    axiosErrorResponse,
    isSuccess,
    isFinished,
    isError,

    loanding,
    data,
    verify,
    error,
  };
}

export default useExternalToken;
